// src/components/shared/BandBadge.jsx
import clsx from 'clsx';

const BAND_STYLES = {
  'X':  'bg-green-100 text-green-800 border-green-200',
  'E+': 'bg-blue-100  text-blue-800  border-blue-200',
  'E':  'bg-gray-100  text-gray-700  border-gray-200',
  'E-': 'bg-red-100   text-red-800   border-red-200',
};

export function BandBadge({ band, label }) {
  const styles = BAND_STYLES[band] || BAND_STYLES['E'];
  return (
    <span className={clsx('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border', styles)}>
      {band}{label ? ` — ${label}` : ''}
    </span>
  );
}

// src/components/shared/PageHeader.jsx
export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex items-start justify-between mb-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

// src/components/shared/StatCard.jsx
export function StatCard({ title, value, subtitle, color = 'blue', icon: Icon }) {
  const colors = {
    blue:   'bg-blue-50  text-blue-600',
    green:  'bg-green-50 text-green-600',
    amber:  'bg-amber-50 text-amber-600',
    red:    'bg-red-50   text-red-600',
    purple: 'bg-purple-50 text-purple-600',
  };
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium text-gray-500">{title}</p>
        {Icon && (
          <div className={clsx('w-9 h-9 rounded-lg flex items-center justify-center', colors[color])}>
            <Icon className="w-5 h-5" />
          </div>
        )}
      </div>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
      {subtitle && <p className="mt-1 text-xs text-gray-500">{subtitle}</p>}
    </div>
  );
}

// src/components/shared/EmptyState.jsx
export function EmptyState({ title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
        <span className="text-2xl">📭</span>
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-1">{title}</h3>
      <p className="text-sm text-gray-500 max-w-sm mb-4">{description}</p>
      {action}
    </div>
  );
}

// src/components/shared/Spinner.jsx
export function Spinner({ size = 'md' }) {
  const s = { sm: 'w-4 h-4', md: 'w-8 h-8', lg: 'w-12 h-12' }[size];
  return <div className={clsx('border-4 border-blue-600 border-t-transparent rounded-full animate-spin', s)} />;
}

// src/components/shared/Modal.jsx
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export function Modal({ open, onClose, title, children, size = 'md' }) {
  const widths = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl', xl: 'max-w-5xl' };
  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className={clsx('w-full bg-white rounded-2xl shadow-2xl', widths[size])}>
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <Dialog.Title className="text-lg font-semibold text-gray-900">{title}</Dialog.Title>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
              <XMarkIcon className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <div className="px-6 py-5">{children}</div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

// src/components/shared/ConfirmModal.jsx
export function ConfirmModal({ open, onClose, onConfirm, title, message, danger }) {
  return (
    <Modal open={open} onClose={onClose} title={title} size="sm">
      <p className="text-sm text-gray-600 mb-6">{message}</p>
      <div className="flex justify-end gap-3">
        <button onClick={onClose} className="btn-secondary">Cancel</button>
        <button
          onClick={() => { onConfirm(); onClose(); }}
          className={danger ? 'btn-danger' : 'btn-primary'}
        >
          Confirm
        </button>
      </div>
    </Modal>
  );
}

// src/components/shared/FlagBadge.jsx
const FLAG_STYLES = {
  error:   'bg-red-50   text-red-700   border-red-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  info:    'bg-blue-50  text-blue-700  border-blue-200',
};

export function FlagBadge({ flag }) {
  return (
    <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border', FLAG_STYLES[flag.severity] || FLAG_STYLES.info)}>
      {flag.severity === 'error' ? '🚨' : flag.severity === 'warning' ? '⚠️' : 'ℹ️'}
      {flag.message}
    </span>
  );
}
